import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Minus, Plus, Trash2, Tag, ArrowRight, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";
import { api, coverSrc, fmtXof, apiError, CATEGORY_SHORT } from "../lib/api";
import { useCurrency } from "../context/CurrencyContext";
import { Price } from "../components/Price";
import { ShareCartButton } from "../components/WhatsApp";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

export default function Cart() {
  const { items, setQty, remove, subtotal, promoCode, setPromoCode } = useCart();
  const { fmt, code, updatedAt } = useCurrency();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [pricing, setPricing] = useState(null);
  const [checking, setChecking] = useState(false);

  const compute = useCallback(async (code) => {
    if (items.length === 0) { setPricing(null); return; }
    try {
      const { data } = await api.post("/promo/validate", {
        items: items.map((i) => ({ product_id: i.product_id, qty: i.qty })),
        code: code || null,
      });
      setPricing(data);
      return data;
    } catch (e) {
      setPricing(null);
      throw e;
    }
  }, [items]);

  useEffect(() => { compute("").catch(() => {}); }, [items]); // eslint-disable-line

  const applyPromo = async () => {
    if (!promoCode.trim()) return;
    setChecking(true);
    try {
      const data = await compute(promoCode.trim());
      toast.success(`Code ${data.promo.code} appliqué : -${data.promo.pct}%`);
    } catch (e) {
      toast.error(apiError(e, "Code promo invalide"));
    } finally {
      setChecking(false);
    }
  };

  const total = pricing ? pricing.total : subtotal;
  const tierDiscount = pricing?.tier_discount || 0;
  const promoDiscount = pricing?.promo_discount || 0;

  return (
    <div className="mx-auto max-w-7xl px-6 py-14" data-testid="cart-page">
      <h1 className="font-display text-3xl font-bold tracking-tight text-navy md:text-4xl">Votre panier</h1>

      {items.length === 0 ? (
        <div className="mt-16 flex flex-col items-center gap-4 text-center">
          <ShoppingCart size={40} className="text-slate-300" />
          <p className="text-slate-500" data-testid="cart-empty">Votre panier est vide.</p>
          <Link to="/catalogue" className="rounded-full bg-ciel px-6 py-3 text-sm font-semibold text-white transition-colors duration-200 hover:bg-sky-700" data-testid="cart-browse">
            Découvrir le catalogue
          </Link>
        </div>
      ) : (
        <div className="mt-10 grid gap-10 lg:grid-cols-3">
          <div className="space-y-4 lg:col-span-2">
            {items.map((i) => (
              <div key={i.product_id} className="flex gap-4 rounded-xl border border-slate-200 bg-white p-4" data-testid={`cart-item-${i.product_id}`}>
                <img src={coverSrc(i.cover)} alt={i.title} className="h-20 w-28 rounded-lg object-cover" />
                <div className="flex flex-1 flex-col">
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-ciel">{CATEGORY_SHORT[i.category] || i.category}</p>
                  <Link to={`/produit/${i.product_id}`} className="mt-0.5 font-display text-sm font-semibold leading-snug text-navy hover:text-ciel">{i.title}</Link>
                  <div className="mt-auto flex items-center justify-between pt-2">
                    <div className="flex items-center gap-2">
                      <button data-testid={`cart-minus-${i.product_id}`} onClick={() => setQty(i.product_id, i.qty - 1)} className="flex h-7 w-7 items-center justify-center rounded-full border border-slate-300 text-slate-600 hover:border-ciel"><Minus size={12} /></button>
                      <span className="w-6 text-center font-mono text-sm font-semibold" data-testid={`cart-qty-${i.product_id}`}>{i.qty}</span>
                      <button data-testid={`cart-plus-${i.product_id}`} onClick={() => setQty(i.product_id, i.qty + 1)} className="flex h-7 w-7 items-center justify-center rounded-full border border-slate-300 text-slate-600 hover:border-ciel"><Plus size={12} /></button>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className="font-display text-sm font-bold text-navy">{fmt(i.price * i.qty)}</span>
                      <button data-testid={`cart-remove-${i.product_id}`} onClick={() => remove(i.product_id)} className="text-slate-400 transition-colors duration-200 hover:text-red-500"><Trash2 size={16} /></button>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <aside className="h-fit rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="cart-summary">
            <h2 className="font-display text-lg font-semibold text-navy">Récapitulatif</h2>

            <div className="mt-5 flex gap-2">
              <Input
                data-testid="promo-input"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                placeholder="Code promo"
                className="font-mono uppercase"
              />
              <Button data-testid="promo-apply" onClick={applyPromo} disabled={checking} variant="outline" className="gap-1.5">
                <Tag size={14} /> {checking ? "…" : "Appliquer"}
              </Button>
            </div>

            <dl className="mt-6 space-y-3 text-sm">
              <div className="flex justify-between text-slate-600">
                <dt>Sous-total</dt><dd className="font-mono" data-testid="cart-subtotal">{fmt(subtotal)}</dd>
              </div>
              {tierDiscount > 0 && (
                <div className="flex justify-between text-emerald-600">
                  <dt>Remise quantitative</dt><dd className="font-mono" data-testid="cart-tier-discount">- {fmt(tierDiscount)}</dd>
                </div>
              )}
              {promoDiscount > 0 && (
                <div className="flex justify-between text-emerald-600">
                  <dt>Code promo ({pricing.promo.code})</dt><dd className="font-mono" data-testid="cart-promo-discount">- {fmt(promoDiscount)}</dd>
                </div>
              )}
              <div className="flex justify-between border-t border-slate-200 pt-3 text-base font-bold text-navy">
                <dt>Total à payer</dt>
                <dd><Price xof={total} className="font-display" testid="cart-total" /></dd>
              </div>
            </dl>

            <Button
              data-testid="cart-checkout"
              onClick={() => navigate(user ? "/checkout" : "/auth", { state: { from: "/checkout" } })}
              className="mt-6 w-full rounded-full bg-ciel py-6 text-sm font-semibold text-white hover:bg-sky-700"
            >
              Passer au paiement <ArrowRight size={15} className="ml-2" />
            </Button>
            {code !== "XOF" && updatedAt && (
              <p className="mt-3 text-center text-[10px] italic text-slate-400" data-testid="cart-rate-note">
                Taux mis à jour le {new Date(updatedAt).toLocaleDateString("fr-FR")} — à titre indicatif
              </p>
            )}
            <p className="mt-3 text-center text-xs text-slate-400">Mobile Money · Carte bancaire · Accès immédiat</p>
            <ShareCartButton items={items} total={total} />
          </aside>
        </div>
      )}
    </div>
  );
}
